/**
 * 充值/订阅状态管理 Store
 * 管理充值计划、订单创建、支付状态等
 */

import { create } from 'zustand';
import { getDepositPlans, createDepositOrder } from '@/api';
import type { 
  PlanWithMethods,
  CreateDepositOrderRequest,
  CreateDepositOrderResult,
} from '@/api';
import { useAuthStore } from './auth';

/**
 * 充值状态接口
 */
export interface DepositState {
  // ==================== 状态 ====================
  
  /** 可用的充值计划列表 */
  plans: PlanWithMethods[];
  /** 当前选中的计划 ID */
  selectedPlanId: string | null;
  /** 当前选中的支付方式 ID */
  selectedPaymentMethodId: string | null;
  /** 当前订单信息 */
  currentOrder: CreateDepositOrderResult | null;
  /** 是否正在加载计划列表 */
  isLoadingPlans: boolean;
  /** 是否正在创建订单 */
  isCreatingOrder: boolean;
  /** 最后更新时间 */
  lastUpdated: number | null;

  // ==================== Actions ====================
  
  /**
   * 设置充值计划列表
   * @param plans - 计划列表
   */
  setPlans: (plans: PlanWithMethods[]) => void;

  /**
   * 设置选中的计划
   * @param planId - 计划 ID
   */
  setSelectedPlan: (planId: string) => void;

  /**
   * 设置选中的支付方式
   * @param paymentMethodId - 支付方式 ID
   */
  setSelectedPaymentMethod: (paymentMethodId: string) => void;

  /**
   * 设置当前订单
   * @param order - 订单信息
   */
  setCurrentOrder: (order: CreateDepositOrderResult | null) => void;

  /**
   * 设置计划列表加载状态
   * @param loading - 是否加载中
   */
  setPlansLoading: (loading: boolean) => void;

  /**
   * 设置订单创建加载状态
   * @param loading - 是否加载中
   */
  setCreatingOrderLoading: (loading: boolean) => void;

  /**
   * 清空所有数据
   */
  clear: () => void;

  /**
   * 刷新充值计划列表
   */
  refreshPlans: () => Promise<void>;

  /**
   * 创建充值订单
   * @param paymentMethodId - 支付方式 ID
   * @returns 创建的订单或 null
   */
  createOrder: (
    paymentMethodId: string
  ) => Promise<CreateDepositOrderResult | null>;

  /**
   * 获取当前选中的计划
   * @returns 计划信息或 null
   */
  getSelectedPlan: () => PlanWithMethods | null;
}

/**
 * 初始状态
 */
const initialState = {
  plans: [],
  selectedPlanId: null,
  selectedPaymentMethodId: null,
  currentOrder: null,
  isLoadingPlans: false,
  isCreatingOrder: false,
  lastUpdated: null,
};

/**
 * 充值/订阅 Store
 * 
 * 职责：
 * - 管理充值计划列表（含支付方式）
 * - 处理订单创建和支付流程
 * - 跟踪订单状态
 * 
 * @example
 * ```ts
 * import { useDepositStore } from '@/stores';
 * 
 * function RechargePage() {
 *   const { plans, selectedPlanId, createOrder } = useDepositStore();
 *   
 *   const handlePay = async () => {
 *     const order = await createOrder(paymentMethodId);
 *     if (order) {
 *       console.log('请向', order.contractAddress, '转入', order.amount, order.tokenSymbol);
 *     }
 *   };
 * }
 * ```
 */
export const useDepositStore = create<DepositState>()(
  (set, get) => ({
      ...initialState,

      setPlans: (plans) => {
        set({
          plans,
          lastUpdated: Date.now(),
        });
      },

      setSelectedPlan: (planId) => {
        set({ selectedPlanId: planId, selectedPaymentMethodId: null });
      },

      setSelectedPaymentMethod: (paymentMethodId) => {
        set({ selectedPaymentMethodId: paymentMethodId });
      },

      setCurrentOrder: (order) => {
        set({ currentOrder: order });
      },

      setPlansLoading: (loading) => {
        set({ isLoadingPlans: loading });
      },

      setCreatingOrderLoading: (loading) => {
        set({ isCreatingOrder: loading });
      },

      clear: () => {
        set(initialState);
      },

      refreshPlans: async () => {
        const authState = useAuthStore.getState();
        
        console.log('[Deposit Store] ====== refreshPlans called ======');
        console.log('[Deposit Store] Auth state:', {
          isAuthenticated: authState.isAuthenticated,
          hasAccessToken: !!authState.accessToken,
          accessToken: authState.accessToken?.substring(0, 20) + '...',
        });
        console.log('[Deposit Store] Current plans count:', get().plans.length);
        
        // 如果没有登录，不获取计划
        if (!authState.isAuthenticated || !authState.accessToken) {
          console.warn('[Deposit Store] User not authenticated, skip fetching plans');
          return;
        }

        console.log('[Deposit Store] Calling API getDepositPlans...');
        set({ isLoadingPlans: true });
        try {
          const result = await getDepositPlans();
          console.log('[Deposit Store] API response ok:', result.ok);
          console.log('[Deposit Store] API response data:', result);
          
          if (result.ok) {
            // /deposit/plans 直接返回启用的 PlanWithMethods[]，无需前端再过滤
            console.log('[Deposit Store] Raw data from API:', result.data);
            console.log('[Deposit Store] Data length:', result.data.length);

            result.data.forEach((plan, index) => {
              console.log(`[Deposit Store] Plan ${index}:`, {
                id: plan.id,
                name: plan.name,
                creditsAmount: plan.creditsAmount,
                hasPaymentMethods: plan.paymentMethods?.length || 0,
              });
            });

            set({
              plans: result.data,
              lastUpdated: Date.now(),
            });

            console.log('[Deposit Store] Store updated successfully');
            console.log('[Deposit Store] New plans count:', get().plans.length);
          } else {
            console.error('[Deposit Store] API returned error:', result.error);
          }
        } catch (error) {
          console.error('[Deposit Store] Failed to refresh plans:', error);
          console.error('[Deposit Store] Error details:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            stack: error instanceof Error ? error.stack : undefined,
          });
        } finally {
          set({ isLoadingPlans: false });
          console.log('[Deposit Store] ====== refreshPlans finished ======');
        }
      },

      createOrder: async (paymentMethodId) => {
        const state = get();

        if (!state.selectedPlanId) {
          console.warn('[Deposit Store] No plan selected');
          return null;
        }

        const plan = state.plans.find((p) => p.id === state.selectedPlanId);
        const paymentMethod = plan?.paymentMethods.find((pm) => pm.id === paymentMethodId);

        if (!plan || !paymentMethod) {
          console.warn('[Deposit Store] Plan or payment method not found', {
            selectedPlanId: state.selectedPlanId,
            paymentMethodId,
          });
          return null;
        }

        // YAML POST /deposit/orders 仅接受 { amount, chainConfigId }；
        // 从选中的 paymentMethod 推导：amount 取计划的 priceAmount，
        // chainConfigId 取 paymentMethod.blockchainId（YAML 类型为 string，需转 number）。
        const chainConfigId = Number(paymentMethod.blockchainId);
        if (!Number.isFinite(chainConfigId)) {
          console.warn('[Deposit Store] Invalid blockchainId', paymentMethod.blockchainId);
          return null;
        }

        const requestData: CreateDepositOrderRequest = {
          amount: paymentMethod.priceAmount,
          chainConfigId,
        };

        set({ isCreatingOrder: true });
        try {
          const result = await createDepositOrder(requestData);
          if (result.ok) {
            set({
              currentOrder: result.data,
              lastUpdated: Date.now(),
            });
            return result.data;
          }
          return null;
        } catch (error) {
          console.error('[Deposit Store] Failed to create order:', error);
          return null;
        } finally {
          set({ isCreatingOrder: false });
        }
      },

      getSelectedPlan: () => {
        const state = get();
        return state.plans.find((plan) => plan.id === state.selectedPlanId) || null;
      },
  })
);

/**
 * 便捷 Hooks
 */

/**
 * 获取充值计划列表
 */
export const useDepositPlans = () => useDepositStore((state) => state.plans);

/**
 * 获取选中的计划 ID
 */
export const useSelectedPlanId = () => useDepositStore((state) => state.selectedPlanId);

/**
 * 获取当前订单
 */
export const useCurrentOrder = () => useDepositStore((state) => state.currentOrder);

/**
 * 获取计划列表加载状态
 */
export const usePlansLoading = () => useDepositStore((state) => state.isLoadingPlans);

/**
 * 获取订单创建加载状态
 */
export const useCreatingOrderLoading = () => useDepositStore((state) => state.isCreatingOrder);
